import { openDocumentOnClick } from '../../helper/documentHelper';
import { withQueryParam } from '../../helper/urlHelper';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Flex,
  Grid,
  Skeleton,
  Steps,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  GetStudyYearById,
  SignRegistrationByOrganization,
} from '../../helper/apiHelper';
import type {
  OrganizationRegistration,
  ProcessSteps,
  ReducedOrganization,
  StudyYear,
} from '../../Types/interfaces';
import { Roles, StepStatus } from '../../Types/interfaces';
import FileUploader from '../FileUploader';
import { formatDate } from '../../Utils/date';
import {
  collaborationDocumentStatus,
  renderStatus,
} from '../../Utils/organizationProcessUtils';
import { QUERY_KEYS } from '../../Constants/queryKeys';

const { useBreakpoint } = Grid;

const ORGANIZATION_SIGNING_STEP =
  'SigningOrganizationRegistrationDocumentByOrganization';

// the steps of the yearly registration, in order, with the texts that explain them
const REGISTRATION_STEPS = [
  {
    stepName: 'OrganizationRegistrationRequested',
    title: 'orgRegistrationCard.stepRegistered',
    description: 'orgRegistrationCard.stepRegisteredDesc',
    waiting: 'orgRegistrationCard.waitingRegistered',
  },
  {
    stepName: 'RegisteringCollaborationDocument',
    title: 'orgRegistrationCard.stepUbbRegistration',
    description: 'orgRegistrationCard.stepUbbRegistrationDesc',
    waiting: 'orgRegistrationCard.waitingUbbRegistration',
  },
  {
    stepName: ORGANIZATION_SIGNING_STEP,
    title: 'orgRegistrationCard.stepYourSignature',
    description: 'orgRegistrationCard.stepYourSignatureDesc',
    waiting: 'orgRegistrationCard.actionNeededDescription',
  },
  {
    stepName: 'SigningOrganizationRegistrationDocumentByLegalDepartment',
    title: 'orgRegistrationCard.stepLegal',
    description: 'orgRegistrationCard.stepLegalDesc',
    waiting: 'orgRegistrationCard.waitingLegal',
  },
  {
    stepName: 'SigningCollaborationDocumentByRector',
    title: 'orgRegistrationCard.stepRector',
    description: 'orgRegistrationCard.stepRectorDesc',
    waiting: 'orgRegistrationCard.waitingRector',
  },
];

type RegistrationState = 'completed' | 'actionNeeded' | 'inProgress';

const getStepStatus = (
  processSteps: ProcessSteps[],
  stepName: string
): StepStatus | undefined =>
  processSteps.find((step) => step.stepName === stepName)?.stepStatus;

const isActive = (status: StepStatus | undefined): boolean =>
  status === StepStatus.initialized || status === StepStatus.updated;

const getRegistrationState = (
  processSteps: ProcessSteps[]
): RegistrationState => {
  if (isActive(getStepStatus(processSteps, ORGANIZATION_SIGNING_STEP))) {
    return 'actionNeeded';
  }
  const allFinished = REGISTRATION_STEPS.every(
    (step) => getStepStatus(processSteps, step.stepName) === StepStatus.finished
  );
  return allFinished ? 'completed' : 'inProgress';
};

interface RegistrationStatusTagProps {
  state: RegistrationState;
  isArchived: boolean;
}

const RegistrationStatusTag = ({
  state,
  isArchived,
}: RegistrationStatusTagProps): React.ReactElement => {
  const { t } = useTranslation();

  if (state === 'completed') {
    return (
      <Tag color="success" icon={<CheckCircleOutlined />}>
        {t('orgRegistrationCard.statusCompleted')}
      </Tag>
    );
  }
  if (isArchived) {
    return <Tag>{t('orgRegistrationCard.statusNotCompleted')}</Tag>;
  }
  if (state === 'actionNeeded') {
    return (
      <Tag color="warning" icon={<ExclamationCircleOutlined />}>
        {t('orgRegistrationCard.statusActionNeeded')}
      </Tag>
    );
  }
  return (
    <Tag color="processing" icon={<ClockCircleOutlined />}>
      {t('orgRegistrationCard.statusInProgress')}
    </Tag>
  );
};

interface OrganizationElementComponentProps {
  organizationRegistration: OrganizationRegistration;
  isArchived: boolean;
  userOrganization: ReducedOrganization;
}

/**
 * One yearly registration (collaboration agreement) of a partner organization.
 * The current one shows the full process; archived ones are a compact row.
 */
const OrganizationElementComponent = ({
  organizationRegistration,
  isArchived,
  userOrganization,
}: OrganizationElementComponentProps): React.ReactElement => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [studyYear, setStudyYear] = useState<StudyYear>();
  const screens = useBreakpoint();
  const queryClient = useQueryClient();

  const processSteps: ProcessSteps[] = organizationRegistration.processSteps;
  const state = getRegistrationState(processSteps);
  const documentStatus = collaborationDocumentStatus(processSteps);
  const documentLink = withQueryParam(
    organizationRegistration.collaborationDocument.url,
    'status',
    documentStatus
  );

  useEffect(() => {
    let cancelled = false;
    GetStudyYearById(
      organizationRegistration.collaborationDocument.studyYearId.toString()
    )
      .then((year) => {
        if (cancelled) return;
        // GetStudyYearById resolves to undefined when the request fails
        if (!year) {
          messageApi.error(t('orgElementComp.studyYearLoadError'));
          return;
        }
        setStudyYear(year);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          messageApi.error(t('orgElementComp.studyYearLoadError'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    organizationRegistration.collaborationDocument.studyYearId,
    messageApi,
    t,
  ]);

  const signByOrganizationManager = async (): Promise<void> => {
    try {
      await SignRegistrationByOrganization(
        organizationRegistration.id,
        organizationRegistration.organizationId
      );
    } catch (error) {
      console.log(error);
      messageApi.error(t('orgElementComp.signError'));
    }
    queryClient.invalidateQueries({
      queryKey: [...QUERY_KEYS.ORGANIZATION_REGISTRATIONS, userOrganization.id],
    });
  };

  if (!studyYear) {
    return <Skeleton active paragraph={{ rows: isArchived ? 1 : 3 }} />;
  }

  const studyYearLabel = t('orgRegistrationCard.studyYear', {
    start: formatDate(studyYear.startDate),
    end: formatDate(studyYear.endDate),
  });

  const registrationInfo = organizationRegistration.registrationNumber ? (
    <Typography.Text type="secondary">
      {t('orgRegistrationCard.registrationNumber', {
        number: organizationRegistration.registrationNumber,
        date: formatDate(organizationRegistration.registrationDate),
      })}
    </Typography.Text>
  ) : null;

  const documentLinkElement = (
    <Typography.Link
      href={documentLink}
      target="_blank"
      onClick={openDocumentOnClick(
        organizationRegistration.collaborationDocument.url,
        documentStatus
      )}
    >
      <FileTextOutlined /> {t('orgElementComp.viewDoc')}
    </Typography.Link>
  );

  if (isArchived) {
    return (
      <Flex
        justify="space-between"
        align="center"
        wrap="wrap"
        gap={8}
        style={{ width: '100%' }}
      >
        <Flex vertical>
          <Typography.Text strong>{studyYearLabel}</Typography.Text>
          {registrationInfo}
        </Flex>
        <Flex align="center" gap={16} wrap="wrap">
          <RegistrationStatusTag state={state} isArchived />
          {documentLinkElement}
        </Flex>
      </Flex>
    );
  }

  const currentStep = REGISTRATION_STEPS.find((step) =>
    isActive(getStepStatus(processSteps, step.stepName))
  );

  const nextStepAlert = (() => {
    if (state === 'completed') {
      return (
        <Alert
          type="success"
          showIcon
          message={t('orgRegistrationCard.completedTitle')}
          description={t('orgRegistrationCard.completedDescription')}
        />
      );
    }
    if (state === 'actionNeeded') {
      return (
        <Alert
          type="warning"
          showIcon
          message={t('orgRegistrationCard.actionNeededTitle')}
          description={
            <Flex vertical gap={4}>
              <span>{t('orgRegistrationCard.actionNeededDescription')}</span>
              <FileUploader
                role={Roles.OrganizationRepresentative}
                apiUrl={'collaboration-documents'}
                documentUrl={organizationRegistration.collaborationDocument.url}
                documentSigningStatus={documentStatus}
                isSignatureDisabled={false}
                alreadySigned={
                  getStepStatus(processSteps, ORGANIZATION_SIGNING_STEP) ===
                  StepStatus.updated
                }
                onFileUpload={signByOrganizationManager}
              />
            </Flex>
          }
        />
      );
    }
    return (
      <Alert
        type="info"
        showIcon
        message={t('orgRegistrationCard.waitingTitle')}
        description={t(
          currentStep?.waiting ?? 'orgRegistrationCard.waitingRegistered'
        )}
      />
    );
  })();

  return (
    <Flex vertical gap={20} style={{ width: '100%' }}>
      {contextHolder}

      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={8}>
        <Flex vertical>
          <Typography.Text strong style={{ fontSize: 16 }}>
            {t('orgRegistrationCard.agreementTitle')}
          </Typography.Text>
          <Typography.Text type="secondary">
            <CalendarOutlined /> {studyYearLabel}
          </Typography.Text>
        </Flex>
        <RegistrationStatusTag state={state} isArchived={false} />
      </Flex>

      {nextStepAlert}

      <Steps
        size="small"
        direction={screens.lg ? 'horizontal' : 'vertical'}
        labelPlacement={screens.lg ? 'vertical' : 'horizontal'}
        items={REGISTRATION_STEPS.map((step) => ({
          title: t(step.title),
          description: t(step.description),
          status: renderStatus(processSteps, step.stepName),
        }))}
      />

      <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
        {documentLinkElement}
        {registrationInfo}
      </Flex>
    </Flex>
  );
};

export default OrganizationElementComponent;
